import React, { useState } from "react";
import { toast } from "sonner";
import { UserPlus, ArrowRight, ArrowLeft } from "lucide-react";
import { safeJson } from "@/lib/api";
import { Spinner } from "@/components/Spinner";

function Field({ label, value, setter, placeholder, required, type = "text" }) {
  return (
    <div className="mb-4">
      <label className="text-sm font-semibold text-[#e2e8f0] mb-1.5 flex items-center gap-1">
        {label}{required && <span className="text-[#ef4444]">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => setter(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-[#1e293b] border border-[#334155] rounded-xl px-4 py-3 text-white outline-none focus:border-[#10b981]"
      />
    </div>
  );
}

export default function ProfileSetup({ apiFetch, onComplete, onBack }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [telegram, setTelegram] = useState("");
  const [discord, setDiscord] = useState("");
  const [xHandle, setXHandle] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !email.includes("@")) {
      toast.error("First name, last name and a valid email are required");
      return;
    }
    setSaving(true);
    try {
      const res = await apiFetch("/api/user/profile/setup", {
        method: "POST",
        body: JSON.stringify({
          first_name: firstName.trim(), last_name: lastName.trim(), email: email.trim(),
          telegram: telegram.trim(), discord: discord.trim(), x_handle: xHandle.trim(),
        }),
      });
      const data = await safeJson(res);
      if (data && data.success) onComplete();
      else toast.error((data && data.detail) || "Failed to save profile");
    } catch {
      toast.error("Network error. Please try again.");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {onBack && (
          <button
            onClick={onBack}
            data-testid="profile-setup-back"
            className="flex items-center gap-1.5 text-[#9ca3af] hover:text-white text-sm font-semibold mb-4 transition-colors"
          >
            <ArrowLeft size={18} /> Back
          </button>
        )}
        <div className="flex items-center justify-center gap-2 mb-2">
          <span className="w-3.5 h-3.5 rounded-full bg-[#10b981]" />
          <span className="w-10 h-0.5 bg-[#334155]" />
          <span className="w-3 h-3 rounded-full bg-[#334155]" />
          <span className="w-10 h-0.5 bg-[#334155]" />
          <span className="w-3 h-3 rounded-full bg-[#334155]" />
        </div>
        <p className="text-center text-[13px] text-[#6b7280] mb-6">Step 1 of 3</p>

        <div className="flex flex-col items-center mb-8">
          <div className="w-20 h-20 rounded-full bg-[#10b981]/10 border-2 border-[#10b981] flex items-center justify-center mb-4">
            <UserPlus size={34} color="#10b981" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-1">Set Up Your Profile</h1>
          <p className="text-[#9ca3af] text-center text-sm">Welcome to BREAKPOINT! Let&apos;s get to know you.</p>
        </div>

        <Field label="First Name" value={firstName} setter={setFirstName} placeholder="John" required />
        <Field label="Last Name" value={lastName} setter={setLastName} placeholder="Smith" required />
        <Field label="Email" value={email} setter={setEmail} placeholder="john@example.com" required type="email" />

        <div className="flex items-center my-5 gap-3">
          <div className="flex-1 h-px bg-[#334155]" />
          <span className="text-[13px] text-[#6b7280]">Optional</span>
          <div className="flex-1 h-px bg-[#334155]" />
        </div>

        <Field label="Telegram" value={telegram} setter={setTelegram} placeholder="@username or chat ID" />
        <Field label="Discord" value={discord} setter={setDiscord} placeholder="username#0000" />
        <Field label="X (Twitter)" value={xHandle} setter={setXHandle} placeholder="@handle" />

        <button
          onClick={submit}
          disabled={saving}
          data-testid="profile-setup-submit"
          className="w-full mt-2 bg-[#10b981] text-black font-bold py-4 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {saving ? <Spinner size={20} color="#000" /> : (<>Continue <ArrowRight size={20} /></>)}
        </button>
        <p className="text-center text-[#6b7280] text-xs mt-4">Your info is stored securely and only visible to you.</p>
      </div>
    </div>
  );
}
